export interface PickerColumn<K extends string = string> {
  key: K;
  /** Name shown in the Columns menu. */
  label: string;
  /** Section heading in the Columns menu. Columns of a group must be adjacent. */
  group?: string;
  defaultVisible?: boolean;
}

export const defaultColumnKeys = <K extends string>(
  catalog: readonly PickerColumn<K>[],
): K[] => catalog.filter((c) => c.defaultVisible).map((c) => c.key);

/**
 * The visible columns for a saved list, in catalog order. `saved` of null means
 * the user never chose, so the defaults apply. Keys no longer in the catalog
 * are dropped, and a list longer than `maxVisible` keeps its first columns.
 */
export const resolveVisibleColumns = <K extends string>(
  catalog: readonly PickerColumn<K>[],
  saved: readonly string[] | null | undefined,
  maxVisible?: number,
): K[] => {
  if (!saved) return defaultColumnKeys(catalog);
  const chosen = new Set(saved);
  const keys = catalog.filter((c) => chosen.has(c.key)).map((c) => c.key);
  return maxVisible ? keys.slice(0, maxVisible) : keys;
};

/** `visible` with `key` flipped, in catalog order. Adding past `maxVisible` is a no-op. */
export const toggleColumn = <K extends string>(
  catalog: readonly PickerColumn<K>[],
  visible: readonly K[],
  key: K,
  maxVisible?: number,
): K[] => {
  if (visible.includes(key)) return visible.filter((k) => k !== key);
  if (maxVisible && visible.length >= maxVisible) return [...visible];
  return resolveVisibleColumns(catalog, [...visible, key]);
};

const headerToken = (header: string): string =>
  header.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * The value of a column in a customer's uploaded row that BITE does not model.
 * Header spellings vary by export, so keys are compared with case, spacing and
 * punctuation ignored ("In-Bond Immediate Transportation Date" matches
 * "inbond immediate transportation date"). The first alias with a non-blank
 * value wins; null when none has one.
 */
export const readUploadedValue = (
  row: Record<string, unknown> | null | undefined,
  aliases: readonly string[],
): string | null => {
  if (!row) return null;
  const keys = Object.keys(row);
  for (const alias of aliases) {
    const token = headerToken(alias);
    for (const k of keys) {
      if (headerToken(k) !== token) continue;
      const raw = row[k];
      const str = raw === undefined || raw === null ? '' : String(raw).trim();
      if (str) return str;
    }
  }
  return null;
};
